import cx from "classnames";
import React from "react";

import { supportsPassive } from "@/mk2/helpers/detects";

import styles from "./StickyBlock.mscss";

interface Props {
    children: React.ReactNode;
    // Maximum height of the outer container. Actual height is computed after mount
    // as min(stickyHeight, max(mainContentHeight / 2, innerContentHeight)) so that
    // the right column never stretches the page beyond its natural content height.
    stickyHeight: number;
    // Pixels below the top of the browser viewport where the inner content sticks.
    topFixedPos?: number;
    className?: string;
}

interface InnerStyle {
    position: "absolute" | "fixed";
    top?: number | null;
    bottom?: number | null;
    width?: number | string;
    visibility?: string;
}

interface State {
    containerHeight: number;
    innerStyle: InnerStyle;
}

/**
 * A container that makes its children sticky for up to `stickyHeight` pixels of scroll.
 *
 * On SSR the container renders with the full `stickyHeight` so server/client output
 * matches. After hydration (componentDidMount) the container height is capped to
 * min(stickyHeight, max(mainContentHeight / 2, innerContentHeight)) so the right
 * column never stretches the page on short posts. The measurement is repeated in
 * componentDidUpdate so it corrects itself once the left column finishes loading.
 *
 * Inspired by AdSlotSticky — same scroll/resize-based positioning logic, but for
 * arbitrary children instead of a single AdSlot.
 */
export class StickyBlock extends React.PureComponent<Props, State> {
    public static defaultProps: Pick<Props, "topFixedPos"> = {
        topFixedPos: 10,
    };

    private static scrollTop(): number {
        return (
            window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
        );
    }

    private static positionInViewport(elem: Element): { top: number; bottom: number } {
        const box = elem.getBoundingClientRect();
        const body = document.body;
        const docElem = document.documentElement;

        const scrollTop = window.pageYOffset || docElem.scrollTop || body.scrollTop;
        const clientTop = docElem.clientTop || body.clientTop || 0;

        return {
            top: Math.round(box.top + scrollTop - clientTop),
            bottom: Math.round(box.bottom + scrollTop - clientTop),
        };
    }

    private container = React.createRef<HTMLDivElement>();
    private inner = React.createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);
        this.state = {
            // Start with the full stickyHeight so SSR and initial client render match.
            // componentDidMount will reduce it if the page content is shorter.
            containerHeight: props.stickyHeight,
            innerStyle: {
                position: "absolute",
                top: 0,
                // Hide until positioned correctly to avoid a visible jump on page-reload
                // when the page is already scrolled (same pattern as AdSlotSticky).
                visibility: "hidden",
            },
        };
    }

    public render() {
        const { children, className } = this.props;
        const { containerHeight, innerStyle } = this.state;

        return (
            <div
                className={cx(styles.StickyBlock, className)}
                style={{ height: containerHeight }}
                ref={this.container}
            >
                <div
                    className={styles.StickyBlock__inner}
                    style={innerStyle as React.CSSProperties}
                    ref={this.inner}
                >
                    {children}
                </div>
            </div>
        );
    }

    public componentDidMount() {
        this.applyContainerHeight();
        const opts = (supportsPassive() ? { passive: true, capture: false } : false) as any;
        window.addEventListener("scroll", this.updatePosition, opts);
        window.addEventListener("resize", this.updatePosition, opts);
    }

    public componentDidUpdate(_prevProps: Props, prevState: State) {
        // Only re-measure container height when this update was NOT caused by a scroll
        // position change. When updatePosition calls setState({innerStyle}), it creates a
        // new object → prevState.innerStyle !== this.state.innerStyle. In that case we skip
        // the expensive DOM measurement. When the left column finishes loading (parent
        // re-renders with new data), innerStyle ref is unchanged → we do measure.
        if (prevState.innerStyle !== this.state.innerStyle) {
            return;
        }
        const newHeight = this.computeContainerHeight();
        if (newHeight !== prevState.containerHeight) {
            this.setState({ containerHeight: newHeight }, this.updatePosition);
        }
    }

    public componentWillUnmount() {
        window.removeEventListener("scroll", this.updatePosition, false);
        window.removeEventListener("resize", this.updatePosition, false);
    }

    // Cap the container height so the right column never stretches the page:
    // – at least as tall as the block's own inner content (no clipping)
    // – at least half the main content height (reasonable sticky coverage)
    // – never more than stickyHeight
    // If main content height is 0 (still loading), skip reduction and keep stickyHeight.
    private computeContainerHeight(): number {
        const mainContent = this.findMainContent();
        const mainContentHeight = (mainContent as HTMLElement)?.offsetHeight ?? 0;
        const innerContentHeight = this.inner.current?.offsetHeight ?? 0;

        if (mainContentHeight === 0) {
            return this.props.stickyHeight;
        }

        return Math.min(
            this.props.stickyHeight,
            Math.max(mainContentHeight / 2, innerContentHeight),
        );
    }

    private applyContainerHeight() {
        const newHeight = this.computeContainerHeight();
        if (newHeight !== this.state.containerHeight) {
            this.setState({ containerHeight: newHeight }, this.updatePosition);
        } else {
            this.updatePosition();
        }
    }

    // Traverse up from the container to find the nearest ancestor that has a
    // direct child with role="main" (the Page__mainContent div). This avoids
    // depending on CSS module class names which are mangled at build time.
    private findMainContent(): Element | null {
        let el = this.container.current?.parentElement;
        while (el) {
            const mainContent = el.querySelector(":scope > [role='main']");
            if (mainContent) {
                return mainContent;
            }
            el = el.parentElement;
        }
        return null;
    }

    private updatePosition = () => {
        if (!this.container.current || !this.inner.current) {
            return;
        }

        const cbox = StickyBlock.positionInViewport(this.container.current);
        const ibox = StickyBlock.positionInViewport(this.inner.current);
        const scrollTop = StickyBlock.scrollTop();
        const fixedTop = scrollTop + this.props.topFixedPos;
        const innerHeight = ibox.bottom - ibox.top;
        const containerWidth = this.container.current.offsetWidth;

        let newStyle: InnerStyle;
        if (fixedTop <= cbox.top) {
            newStyle = { position: "absolute", top: 0, bottom: null, width: "100%" };
        } else if (fixedTop + innerHeight >= cbox.bottom) {
            newStyle = { position: "absolute", top: null, bottom: 0, width: "100%" };
        } else {
            // Explicit px width needed: position:fixed takes width from viewport,
            // not from parent container.
            newStyle = {
                position: "fixed",
                top: this.props.topFixedPos,
                bottom: null,
                width: containerWidth,
            };
        }

        // Only setState when something actually changed — prevents infinite loop in
        // componentDidUpdate (setState → re-render → componentDidUpdate → setState...).
        const cur = this.state.innerStyle;
        if (
            newStyle.position !== cur.position ||
            newStyle.top !== cur.top ||
            newStyle.bottom !== cur.bottom ||
            newStyle.width !== cur.width ||
            cur.visibility !== undefined // first call: clears the initial visibility:hidden
        ) {
            this.setState({ innerStyle: newStyle });
        }
    };
}
